 
/**
 * S07 · Voice review — playback + editable transcript for a freshly
 * recorded voice capture. Audio streams from OPFS via `useBlobUrl`.
 *
 * The transcript arrives from the server, not the device: the extraction that
 * already runs on upload returns it alongside the field suggestions, on the
 * same call and the same audio. Nothing is transcribed locally, so the box is
 * empty until the capture has uploaded and one pull has come back.
 *
 * Editing always wins. The server fills a blank transcript and never replaces
 * one that has text in it, so a correction typed here survives every
 * subsequent run — see `services/ai/extraction.py`.
 */
import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Btn,
  Chip,
  GestureBar,
  I,
  Ico,
  ScreenFrame,
  StatusBar,
  T,
  Waveform,
} from '@/components/primitives';
import { useCapture, useUpdateTranscriptMutation } from '@kapture/client-core';
import { useBlobUrl } from '@/lib/device/useBlobUrl';

export const Route = createFileRoute('/sessions/$sessionId/capture/voice/review/$captureId')({
  component: VoiceReviewPage,
  staticData: { mobile: true },
});

function formatTime(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function VoiceReviewPage() {
  const { sessionId, captureId } = Route.useParams();
  const navigate = useNavigate();
  const { data: capture } = useCapture(captureId);
  const updateTranscript = useUpdateTranscriptMutation();

  const blobPath = capture?.kind === 'voice' ? capture.blobPath : null;
  const url = useBlobUrl(blobPath);
  const [transcript, setTranscript] = useState('');
  const [playing, setPlaying] = useState(false);
  const [progressMs, setProgressMs] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (capture?.kind === 'voice' && capture.transcript != null) {
      setTranscript(capture.transcript);
    }
  }, [capture]);

  if (capture && capture.kind !== 'voice') {
    return (
      <ScreenFrame tone="light">
        <StatusBar />
        <Centered>That capture isn't a voice note.</Centered>
      </ScreenFrame>
    );
  }

  const durationMs = capture?.kind === 'voice' ? capture.durationMs : 0;

  function togglePlay() {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      void audio.play();
    } else {
      audio.pause();
    }
  }

  async function save() {
    await updateTranscript.mutateAsync({ captureId, transcript: transcript.trim() });
    navigate({
      to: '/sessions/$sessionId/captures/$captureId',
      params: { sessionId, captureId },
    });
  }

  function rerecord() {
    navigate({ to: '/sessions/$sessionId/capture/voice', params: { sessionId } });
  }

  const progress = durationMs > 0 ? Math.min(1, progressMs / durationMs) : 0;

  return (
    <ScreenFrame tone="light">
      <StatusBar />
      <div
        style={{
          padding: '8px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${T.stroke}`,
        }}
      >
        <button
          type="button"
          onClick={() =>
            navigate({ to: '/sessions/$sessionId', params: { sessionId } })
          }
          aria-label="Back"
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: T.text,
            display: 'flex',
          }}
        >
          <Ico d={I.back} size={20} />
        </button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: T.font }}>Voice note</div>
          <div
            style={{
              fontSize: 10,
              color: T.textMute,
              fontFamily: T.mono,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            {capture ? 'Saved · tap play to review' : 'Loading…'}
          </div>
        </div>
        <button
          type="button"
          onClick={() => void save()}
          aria-label="Save transcript"
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: updateTranscript.isPending ? 'not-allowed' : 'pointer',
            color: T.brand,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: T.font,
            opacity: updateTranscript.isPending ? 0.5 : 1,
          }}
        >
          {updateTranscript.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div style={{ padding: 16, background: T.surface2 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            type="button"
            onClick={togglePlay}
            disabled={!url}
            aria-label={playing ? 'Pause' : 'Play'}
            style={{
              width: 44,
              height: 44,
              borderRadius: 22,
              background: T.brand,
              color: T.brandInk,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              cursor: url ? 'pointer' : 'not-allowed',
              opacity: url ? 1 : 0.5,
            }}
          >
            <Ico d={playing ? I.pause : I.play} size={20} />
          </button>
          <div style={{ flex: 1 }}>
            <Waveform bars={30} active={progress} color={T.ink} bg={T.stroke} h={32} />
          </div>
          <div
            style={{
              fontSize: 11,
              color: T.textDim,
              fontFamily: T.mono,
              fontWeight: 500,
            }}
          >
            {formatTime(progressMs)} / {formatTime(durationMs)}
          </div>
        </div>
        {url && (
          <audio
            ref={audioRef}
            src={url}
            preload="auto"
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onTimeUpdate={(e) => setProgressMs(Math.round(e.currentTarget.currentTime * 1000))}
            onEnded={() => {
              setPlaying(false);
              setProgressMs(0);
            }}
            style={{ display: 'none' }}
          />
        )}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        <div
          style={{
            fontSize: 11,
            color: T.textMute,
            fontFamily: T.font,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Ico d={I.sparkle} size={12} fill={T.brand} stroke={T.brand} />
          Transcript · editable
        </div>
        <textarea
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
          placeholder="Type what was said, or wait for the transcript to arrive after this uploads. Anything you write here is kept — a transcription will not overwrite it."
          style={{
            width: '100%',
            minHeight: 180,
            fontSize: 15,
            fontFamily: T.font,
            lineHeight: 1.55,
            color: T.text,
            border: `1px solid ${T.stroke}`,
            borderRadius: 10,
            outline: 'none',
            padding: 12,
            resize: 'vertical',
            background: T.surface,
          }}
        />
        <div style={{ marginTop: 10 }}>
          <Chip tone="brandSoft" size="sm">
            Audio saved · {Math.round((capture?.byteSize ?? 0) / 1024)} KB
          </Chip>
        </div>
      </div>

      <div
        style={{
          padding: '10px 16px 12px',
          borderTop: `1px solid ${T.stroke}`,
          display: 'flex',
          gap: 8,
        }}
      >
        <Btn tone="light" size="md" icon={<Ico d={I.mic} size={16} />} onClick={rerecord}>
          Re-record
        </Btn>
        <Btn
          tone="primary"
          size="md"
          full
          onClick={() => void save()}
          iconRight={<Ico d={I.arrow} size={16} />}
          disabled={updateTranscript.isPending}
          style={{ opacity: updateTranscript.isPending ? 0.5 : 1 }}
        >
          {updateTranscript.isPending ? 'Saving…' : 'Done'}
        </Btn>
      </div>
      <GestureBar />
    </ScreenFrame>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        flex: 1,
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        color: T.textDim,
        fontFamily: T.font,
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}
